/**
 * Phase-10 — FootGuesser "Icon" player card.
 *
 * FUT-Icon-inspired gothic shield silhouette in neon-green-on-dark, NOT a
 * plain rounded rectangle. Frame is a reusable inline-SVG component
 * (<CardFrame>) so Quiz reveals, Career Path stints, results, etc. can share
 * the same silhouette.
 *
 * Layout (over the 5:7 frame, normalised to percentages so any size scales):
 *   • Top-left cluster: flag bezel + club badge bezel, stacked vertically.
 *   • Center: large player portrait, neon-tinted, photo or initials fallback.
 *   • Bottom: BIG UPPERCASE NAME, secondary position · team line.
 *   • Bottom-right: small rating disc (derived deterministically).
 *
 * Backwards compatible: same prop API as the old PlayerCard / TeamCard.
 *   props: player, team, startYear, endYear, variant, onClick, testId,
 *          index, className, size?, autoFitName?
 */
import { motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { useId } from "react";
import { countryFlag, yearRange } from "../lib/derive";

const NEON       = "#00FF87";
const NEON_DEEP  = "#0F6B47";
const FILL_TOP   = "#0E1A14";
const FILL_BOT   = "#1A2421";

/**
 * Gothic-shield silhouette + ornamental flourish.
 *
 * Path is drawn inside viewBox 0 0 100 140 (5:7). The waist narrows ~28% on
 * each side, top has a soft crown bump, bottom has a gentle scoop. Two small
 * triangular notches mid-edge act as the "FUT trim" flourish.
 */
function ShieldPath({ inset = 0, ...rest }) {
  // Inset is used to draw the inner trim line slightly inside the outer shape.
  const i = inset;
  const d = `
    M ${50}            ${4 + i}
    C ${58 + i*0.2}    ${4 + i}    ${62 + i*0.2}   ${5 + i}    ${66 + i*0.2}  ${6 + i}
    L ${88 - i}        ${9 + i}
    C ${94 - i}        ${10 + i}   ${96 - i}       ${15 + i}   ${95 - i}      ${22 + i}
    L ${93 - i}        ${52 + i}
    L ${91 - i}        ${60 + i}
    L ${94 - i}        ${66 + i}
    L ${91 - i}        ${72 + i}
    C ${90 - i}        ${85}       ${86 - i}       ${98 - i*0.5} ${78 - i}    ${110 - i*0.5}
    C ${70 - i*0.4}    ${122 - i*0.4} ${60 - i*0.3} ${132 - i*0.3} ${50}      ${138 - i}
    C ${40 + i*0.3}    ${132 - i*0.3} ${30 + i*0.4} ${122 - i*0.4} ${22 + i}  ${110 - i*0.5}
    C ${14 + i}        ${98 - i*0.5} ${10 + i}      ${85}         ${9 + i}    ${72 + i}
    L ${6 + i}         ${66 + i}
    L ${9 + i}         ${60 + i}
    L ${7 + i}         ${52 + i}
    L ${5 + i}         ${22 + i}
    C ${4 + i}         ${15 + i}    ${6 + i}        ${10 + i}    ${12 + i}   ${9 + i}
    L ${34 + i}        ${6 + i}
    C ${38 + i*0.2}    ${5 + i}     ${42 + i*0.2}   ${4 + i}     ${50}       ${4 + i}
    Z
  `.replace(/\s+/g, " ").trim();
  return <path d={d} {...rest} />;
}

/**
 * Reusable shield frame. Renders the SVG silhouette + neon trim + background
 * gradient + diagonal "torn paper" pattern. Children are rendered inside an
 * absolutely-positioned overlay so callers can drop in HTML at percentages.
 */
export function CardFrame({
  children,
  variant = "default",          // "default" | "correct" | "skeleton" | "compact"
  className = "",
  ariaLabel,
  id: explicitId,
}) {
  // Stable ids for <clipPath> / <pattern> / <linearGradient>. Each card needs
  // its own to avoid clashes when the page mounts several frames. useId() is
  // stable across renders, deterministic in tests, and unique per instance.
  const autoId = useId().replace(/[:]/g, "");
  const baseId = explicitId ?? `cf-${autoId}`;
  const clipId    = `${baseId}-clip`;
  const gradId    = `${baseId}-grad`;
  const patternId = `${baseId}-pat`;

  const isCorrect = variant === "correct";

  return (
    <div
      className={`relative w-full ${className}`}
      style={{ aspectRatio: "5 / 7" }}
      aria-label={ariaLabel}
    >
      <svg
        viewBox="0 0 100 140"
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 w-full h-full"
        style={{
          filter: isCorrect
            ? "drop-shadow(0 0 26px rgba(0,255,135,0.75))"
            : "drop-shadow(0 0 18px rgba(0,255,135,0.45))",
        }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={FILL_TOP} />
            <stop offset="100%" stopColor={FILL_BOT} />
          </linearGradient>
          {/* Diagonal torn-paper streak pattern, very low opacity. */}
          <pattern id={patternId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(35)">
            <line x1="0" y1="0" x2="0" y2="6" stroke={NEON} strokeWidth="0.5" opacity="0.06" />
            <line x1="3" y1="0" x2="3" y2="6" stroke={NEON} strokeWidth="0.2" opacity="0.04" />
          </pattern>
          <clipPath id={clipId}>
            <ShieldPath />
          </clipPath>
        </defs>
        {/* Fill */}
        <ShieldPath fill={`url(#${gradId})`} />
        <ShieldPath fill={`url(#${patternId})`} />
        {/* Outer trim (neon) */}
        <ShieldPath fill="none" stroke={NEON} strokeWidth="1.4"
                    strokeLinejoin="round" opacity={isCorrect ? 1 : 0.9} />
        {/* Inner trim */}
        <ShieldPath inset={2.2} fill="none" stroke={NEON_DEEP}
                    strokeWidth="0.55" strokeLinejoin="round" opacity="0.85" />
        {/* Decorative side flourish — small triangle notches */}
        <g opacity="0.7">
          <path d="M 4 70 L 8 73 L 4 76 Z" fill={NEON_DEEP} />
          <path d="M 96 70 L 92 73 L 96 76 Z" fill={NEON_DEEP} />
        </g>
        {/* Top crown spark */}
        <circle cx="50" cy="5" r="0.9" fill={NEON} />
      </svg>

      {/* HTML overlay — children are absolutely positioned in percentages
          inside the shield bounds. We deliberately do NOT clip with CSS
          `clipPath: path()` here because CSS path() uses pixel coordinates
          and would not scale with the responsive frame; the silhouette is
          already drawn behind, and child elements stay inside its bounds. */}
      <div className="absolute inset-0">
        {children}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

function PlayerPortrait({ player }) {
  const initials = (player?.name || "?")[0]?.toUpperCase();
  return (
    <div className="absolute left-1/2 -translate-x-1/2 top-[7%] w-[58%]"
         style={{ aspectRatio: "1 / 1" }}>
      <div className="absolute inset-0 rounded-full"
           style={{ background: "radial-gradient(circle at 50% 35%, rgba(0,255,135,0.22), transparent 62%)" }} />
      {player?.image_url ? (
        <img
          src={player.image_url}
          alt={player.name || ""}
          referrerPolicy="no-referrer"
          data-testid="player-card-photo"
          className="relative w-full h-full object-cover rounded-full ring-1 ring-[#0F6B47]"
          style={{ filter: "saturate(0.65) contrast(1.05)" }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div
          data-testid="player-card-photo-fallback"
          className="relative w-full h-full rounded-full grid place-items-center text-[36cqw] font-black text-[#0F6B47]"
          style={{ background: "rgba(0,255,135,0.05)" }}
        >
          {initials}
        </div>
      )}
      {/* neon tint overlay */}
      <div className="absolute inset-0 rounded-full pointer-events-none"
           style={{ background: "linear-gradient(180deg, rgba(0,255,135,0.06) 0%, rgba(0,255,135,0.0) 60%)" }} />
    </div>
  );
}

/* Bottom info bar — 3 cells separated by thin neon-green vertical dividers.
   Left = club logo + name; Center = position chip + year range;
   Right = country flag + ISO code. Cells truncate; nothing wraps. */
function InfoBar({ player, team, startYear, endYear }) {
  const pos = (player?.position || "").toUpperCase().slice(0, 3);
  const yr  = yearRange(startYear, endYear);
  const flag = countryFlag(player?.nationality);
  const cc   = (player?.nationality || "").toUpperCase();
  const cell = "flex-1 min-w-0 px-[3%] flex items-center gap-[4%]";
  const sep  = { borderLeft: `1px solid ${NEON_DEEP}`, opacity: 0.55 };
  const txt  = { fontSize: "clamp(0.45rem, 2.4cqw, 0.7rem)", letterSpacing: "0.12em", color: "#7DD3B2" };
  return (
    <div data-testid="player-card-info-bar"
         className="absolute left-[8%] right-[8%] bottom-[14%] flex items-center"
         style={{ height: "11%" }}>
      {/* Club */}
      <div className={cell} data-testid="player-card-club">
        <div className="rounded-full bg-[#0E1A14] ring-1 ring-[#0F6B47] grid place-items-center overflow-hidden shrink-0"
             style={{ width: "min(18cqw, 28px)", height: "min(18cqw, 28px)" }}>
          {team?.logo_url ? (
            <img src={team.logo_url} alt="" referrerPolicy="no-referrer"
                 className="w-[78%] h-[78%] object-contain"
                 onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <span data-testid="player-card-club-fallback"
                  className="w-[78%] h-[78%] rounded-full grid place-items-center text-[10px] font-black uppercase text-black"
                  style={{ background: team?.badge_color || NEON }}>
              {(team?.name || "—")[0]?.toUpperCase()}
            </span>
          )}
        </div>
        <span className="truncate font-bold uppercase" style={txt}>{team?.name || "—"}</span>
      </div>
      {/* Position + year range */}
      <div className={cell} style={sep} data-testid="player-card-pos">
        {pos && (
          <span className="font-black uppercase shrink-0"
                style={{ ...txt, color: NEON, letterSpacing: "0.18em" }}>{pos}</span>
        )}
        {yr && (
          <span className="truncate tabular-nums font-mono opacity-80" style={txt}>{yr}</span>
        )}
      </div>
      {/* Country */}
      <div className={cell} style={sep} data-testid="player-card-flag">
        <span className="shrink-0 leading-none"
              style={{ fontSize: "min(20cqw, 22px)" }}>
          {flag || <span style={{ fontSize: "10px" }} className="text-zinc-500">??</span>}
        </span>
        {cc && <span className="truncate font-bold" style={txt}>{cc}</span>}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

export function PlayerCard({
  player,
  team,
  startYear,
  endYear,
  variant = "default",
  onClick,
  testId,
  index = 0,
  className = "",
}) {
  const reduceMotion = useReducedMotion();

  if (variant === "skeleton") {
    return (
      <CardFrame variant="skeleton" className={className}>
        <div
          data-testid={testId || "player-card-skeleton"}
          className="absolute inset-0 grid place-items-center"
          style={{ background: "rgba(255,255,255,0.02)" }}
        >
          <div className="w-[58%] aspect-square rounded-full bg-white/5 animate-pulse" />
        </div>
      </CardFrame>
    );
  }

  const p = player || {};
  const isCorrect = variant === "correct";
  const isCompact = variant === "compact";

  return (
    <motion.button
      type="button"
      onClick={onClick}
      data-testid={testId || `player-card-${p._id || index}`}
      initial={reduceMotion ? false : { opacity: 0, y: 18, rotateX: -6 }}
      animate={reduceMotion ? {} : { opacity: 1, y: 0, rotateX: 0 }}
      transition={{ delay: 0.06 * index, duration: 0.42, ease: [0.2, 0.7, 0.2, 1] }}
      whileHover={reduceMotion ? {} : { y: -6, rotate: 1.5, transition: { duration: 0.22 } }}
      whileTap={reduceMotion ? {} : { scale: 0.985 }}
      className={`group relative block w-full text-left bg-transparent border-0 p-0 ${className}`}
      style={{ transformStyle: "preserve-3d", containerType: "inline-size" }}
    >
      <CardFrame variant={isCorrect ? "correct" : "default"} ariaLabel={p.name}>
        {/* Center portrait (no top-left flag/club cluster — moved to bottom info bar). */}
        <PlayerPortrait player={p} />

        {/* Player name — sits just above the bottom info bar. Auto-fits via
            container queries; oversized names ellipsize to one line. */}
        <div
          className="absolute left-[6%] right-[6%]"
          style={{ bottom: isCompact ? "29%" : "27%" }}
        >
          <div
            data-testid="player-card-name"
            className="text-center font-black uppercase truncate"
            style={{
              color: NEON,
              letterSpacing: "0.04em",
              // Phase-10.2: down ~30% from previous 11cqw / 26px peak so
              // long 2-word names like "Marcelo Brozović" / "Mateo Kovačić"
              // fit on one line at the sm/md card sizes.
              fontSize: "clamp(0.7rem, 3.2cqw, 1.05rem)",
              lineHeight: 1.05,
              textShadow: "0 0 12px rgba(0,255,135,0.35)",
            }}
          >
            {p.name || "—"}
          </div>
        </div>

        {/* Bottom info bar — club | position+years | country flag. */}
        <InfoBar player={p} team={team} startYear={startYear} endYear={endYear} />

        {/* Correct ribbon */}
        {isCorrect && (
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, scale: 0.6, y: -6 }}
            animate={reduceMotion ? {} : { opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 350 }}
            data-testid="player-card-correct-ribbon"
            className="absolute flex items-center gap-1 rounded-full bg-[var(--accent)] text-black font-black uppercase shadow-[0_4px_18px_rgba(0,255,135,0.55)]"
            style={{
              top: "6%", right: "8%",
              padding: "0.5cqw 1.6cqw",
              fontSize: "min(4.5cqw, 11px)",
              letterSpacing: "0.15em",
            }}
          >
            <Check size={10} strokeWidth={3.5} />
            correct
          </motion.div>
        )}
      </CardFrame>
    </motion.button>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Team variant of the same gothic frame. Used by Dashboard / Profile /
 * PublicProfile "favourite club" preview. Same prop shape as before, plus
 * a tiny inline mode for the existing strip-row callsites.
 */
export function TeamCard({ team, className = "", testId, layout = "row" }) {
  if (!team) return null;

  // Row layout is the old strip used in Dashboard / Profile / PublicProfile.
  // Kept identical so existing pages render unchanged.
  if (layout === "row") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        data-testid={testId || "team-card"}
        className={`flex items-center gap-3 px-4 py-3 rounded-2xl border border-white/10 bg-gradient-to-r from-[var(--surface-2)] to-[var(--surface)] ${className}`}
      >
        {team.logo_url ? (
          <img src={team.logo_url} alt="" className="w-10 h-10 object-contain"
               referrerPolicy="no-referrer" />
        ) : (
          <span className="w-10 h-10 rounded-full inline-block"
                style={{ background: team.badge_color || "#666" }} />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[var(--accent)]">
            {team.type === "national_team" ? "national team" : "favourite club"}
          </div>
          <div className="text-sm font-bold truncate">{team.name}</div>
          {team.country && (
            <div className="text-[11px] text-zinc-500">{team.country}</div>
          )}
        </div>
      </motion.div>
    );
  }

  // "card" layout — gothic shield with the team crest centered.
  return (
    <CardFrame ariaLabel={team.name} className={className}>
      <div className="absolute inset-0 grid place-items-center">
        {team.logo_url ? (
          <img src={team.logo_url} alt={team.name} className="w-[56%] h-[56%] object-contain drop-shadow-[0_0_22px_rgba(0,255,135,0.35)]"
               referrerPolicy="no-referrer" />
        ) : (
          <span
            className="w-[56%] aspect-square rounded-full grid place-items-center font-black text-black"
            style={{ background: team.badge_color || NEON, fontSize: "26cqw" }}
            data-testid="team-card-fallback"
          >
            {(team.name || "?")[0]?.toUpperCase()}
          </span>
        )}
      </div>
      <div className="absolute left-[8%] right-[8%] bottom-[12%] text-center">
        <div className="font-black uppercase truncate"
             style={{ color: NEON, fontSize: "min(10cqw, 24px)", letterSpacing: "0.06em" }}>
          {team.name}
        </div>
        {team.country && (
          <div className="mt-1 text-[10px] tracking-[0.2em] uppercase" style={{ color: "#7DD3B2" }}>
            {team.country}
          </div>
        )}
      </div>
    </CardFrame>
  );
}

export default PlayerCard;
